import { useStore as useJotaiStore, useSetAtom } from 'jotai';
import Router from 'next/router';
import { type MutableRefObject, useEffect, useRef } from 'react';

import {
  chronicleDeviceRecordingInfoAtom,
  chronicleDeviceStateAtom,
  chronicleLiveOfflineAtom,
  chroniclePureOfflineSessionsAtom,
  chronicleReconnectedPendingEndAtom,
  chronicleRecordingEndReasonAtom,
  chronicleRemoteStatusCardAtom,
  chronicleSessionIdAtom,
} from '@/atoms/chronicle';
import { useIsNewSessionListEnabled } from '@/features/flags';
import { chronicleBridge } from '@/tauri/chronicle';
import { listenChronicleRecordingRecovered } from '@/tauri/chronicle/events';
import { trackHardwareRecordingRecovered } from '@/utils/analytics/track-hardware-events';
import { chronicleLogger as logger } from '@/utils/logger/chronicle-logger';
import { getCurrentSessionId } from '@heidi/runtime/shell';
import { buildSessionPath } from '@heidi/session/contracts';
import { activeSessionIdAtom } from '@heidi/session/data';

import {
  processRecordingRecovered,
  seedRecoveredTimer,
} from '../utils/process-recording-recovered';

/**
 * APP-7868: consume `chronicle:recording_recovered` — clear the live-offline
 * hold, re-seed the timer to the device's authoritative duration, restore the
 * session/recording atoms. Mounted once by ChronicleStateProvider.
 *
 * `wasRecordingRef` is the provider's recording→non-recording transition ref,
 * shared with the state_changed handler.
 */
export function useChronicleRecordingRecoveredListener(
  wasRecordingRef: MutableRefObject<boolean>
) {
  const setLiveOffline = useSetAtom(chronicleLiveOfflineAtom);
  const setChronicleSessionId = useSetAtom(chronicleSessionIdAtom);
  const setActiveSessionId = useSetAtom(activeSessionIdAtom);
  const setDeviceRecordingInfo = useSetAtom(chronicleDeviceRecordingInfoAtom);
  const setReconnectedPendingEnd = useSetAtom(chronicleReconnectedPendingEndAtom);
  const setPureOfflineSessions = useSetAtom(chroniclePureOfflineSessionsAtom);
  const setRecordingEndReason = useSetAtom(chronicleRecordingEndReasonAtom);
  const setRemoteStatusCard = useSetAtom(chronicleRemoteStatusCardAtom);
  const jotaiStore = useJotaiStore();
  // Ref-mirrored so the long-lived Tauri listener reads the live flag without
  // re-subscribing on flag flips (same pattern as useChronicleSessionControl).
  const isNewSessionListEnabled = useIsNewSessionListEnabled();
  const isNewSessionListEnabledRef = useRef(isNewSessionListEnabled);
  isNewSessionListEnabledRef.current = isNewSessionListEnabled;
  // flow 2: session id already auto-navigated to — a duplicate bound
  // `created_offline` emit for the same session must not navigate twice.
  const navigatedPureOfflineSessionRef = useRef<string | null>(null);

  useEffect(() => {
    if (!chronicleBridge.isAvailable()) return;

    let unlistenFn: (() => void) | null = null;
    // Same teardown-race guard as the state_changed listener (APP-7607).
    let cancelled = false;

    const setup = async () => {
      try {
        const unlisten = await listenChronicleRecordingRecovered((event) => {
          // Log identifiers only — never the full payload or fileName.
          logger.info({
            msg: 'Chronicle recording_recovered event received',
            sessionId: event.payload.sessionId,
            durationSecs: event.payload.durationSecs,
            isPaused: event.payload.isPaused,
            recoveredMatchVia: event.payload.recoveredMatchVia,
            recoveredFormat: event.payload.recoveredFormat,
            // Device-generated REC_<YYYYMMDD>_<HHmmss> (chronicle/filename.rs):
            // a prefix and a timestamp, no user-supplied component. This PR
            // minimizes IDENTIFIERS, not strings, so it stays.
            fileName: event.payload.fileName,
            // SCREAMING_SNAKE enum (CHRONICLE_RECOVER_BIND_FAILED, ...): zero
            // identifier content, and without it a failed bind reaches Datadog
            // with no reason code at all.
            errorCode: event.payload.errorCode,
          });

          const result = processRecordingRecovered(event.payload, {
            recordingInfo: jotaiStore.get(chronicleDeviceRecordingInfoAtom),
            liveOffline: jotaiStore.get(chronicleLiveOfflineAtom),
            currentSessionId: jotaiStore.get(chronicleSessionIdAtom),
            deviceId: jotaiStore.get(chronicleDeviceStateAtom).deviceId,
            nowMs: Date.now(),
          });

          if (result.action === 'ignore') {
            // Stale recovery (APP-7879): a late bind for a stopped file must
            // not merge its session onto the recording that replaced it.
            // This branch fires exactly when the two names disagree, so
            // without them the mismatch it reports is un-debuggable.
            logger.info({
              msg: 'Chronicle recording_recovered ignored: stale fileName',
              sessionId: event.payload.sessionId,
              eventFileName: event.payload.fileName,
              currentFileName: jotaiStore.get(chronicleDeviceRecordingInfoAtom)?.fileName,
            });
            return;
          }

          // reflect the RECONNECTED status card on ANY applied
          // recovered event so BOTH flows surface it — the live-recovery
          // reconnect (a hold was present) and the pure-offline connect (app
          // connected to an already-recording device, no prior hold). Rust
          // gates both `recording_recovered` emits behind `recording_pre_existing`
          // (auto_session.rs), so a fresh online recording never reaches here.
          //
          // The window is session-KEYED so a newer session arming mid-drain
          // cannot inherit it. `result.sessionId` is only carried by a bound
          // emit, so fall back to the ids a deferred/unbound emit still has —
          // the same two the window's session-scoped reader
          // (`useIsRemoteReconnectedSession`) already matches against. If none
          // resolves (a deferred pure-offline emit before its bind), leave an
          // already-armed window untouched rather than writing null — wiping
          // it while also clearing liveOffline would leave no hold AND no
          // reconnect window between emit and bound retry, so a coincident
          // connected/syncing broadcast would finalize as normal_stop.
          const resolvedReconnectedSessionId =
            result.sessionId ??
            jotaiStore.get(chronicleSessionIdAtom) ??
            jotaiStore.get(chronicleDeviceRecordingInfoAtom)?.sessionId ??
            null;
          if (resolvedReconnectedSessionId !== null) {
            setReconnectedPendingEnd(resolvedReconnectedSessionId);
          }
          // Fresh reconnect window — drop any stale dismissal so the RECONNECTED
          // card shows even if a prior card was dismissed.
          setRemoteStatusCard((prev) => ({ ...prev, dismissedKey: null }));

          // Any applied recovered emit (deferred/failed bind included) means
          // the device is reconnected, controllable, and RECORDING — clear the
          // hold and keep the recording transition armed so a second drop
          // before a deferred retry lands still re-holds.
          setLiveOffline(null);
          // (defense-in-depth): clear any stale `ble_disconnect`
          // end-reason stamped by the drop that preceded this recovery. The
          // device is recording again, so a later spurious recording-info null
          // must not be classified as an interrupted stop.
          setRecordingEndReason(null);
          wasRecordingRef.current = true;
          // Only a successful bind carries a session to restore; a failed bind
          // during a hold must not clobber the session id or re-seed the timer.
          if (result.bound) {
            // `created_offline` means the session was CREATED at this
            // bind — no online session ever existed for the recording, so it
            // was never interrupted. Mark it so the "Session Interrupted"
            // surfaces suppress while its recovery-bind row sits in the
            // interrupted set. Idempotent/filename_match binds are NOT marked:
            // they can be re-binds of a genuinely interrupted (flow 1) session
            // after an app restart, and mis-marking one would hide a real
            // interruption. (A restart mid-window of a pure-offline session
            // therefore re-binds unmarked — accepted edge, badge shows until
            // its sync completes.)
            if (event.payload.recoveredMatchVia === 'created_offline') {
              const sessionId = result.sessionId;
              if (sessionId) {
                setPureOfflineSessions((prev) => {
                  if (prev.has(sessionId)) return prev;
                  const next = new Set(prev);
                  next.add(sessionId);
                  return next;
                });
                // flow 2 (iOS parity): the session was CREATED by this
                // bind, so the user is necessarily viewing some OTHER session —
                // where the header recording controls now misleadingly render as
                // if THAT session resumed. Move them to the created session.
                // Bound `created_offline` only: flow-1 binds (idempotent/
                // filename_match/active/pending) resume in place, and the
                // deferred initial emit also carries `created_offline` but is
                // unbound (no id yet) — its retry re-emits bound and lands here.
                // First such emit only; a duplicate must not yank navigation.
                if (navigatedPureOfflineSessionRef.current !== sessionId) {
                  navigatedPureOfflineSessionRef.current = sessionId;
                  if (getCurrentSessionId() !== sessionId) {
                    logger.info({
                      msg: 'Chronicle pure-offline bind: navigating to created session',
                      sessionId,
                    });
                    // Flag-correct route, mirroring useChronicleSessionControl's
                    // post-stop navigation — avoids the `/scribe/session` →
                    // `/scribe/sessions` redirect (full page refresh).
                    void Router.push(
                      buildSessionPath(sessionId, isNewSessionListEnabledRef.current)
                    );
                  }
                }
              }
            }
            setChronicleSessionId(result.sessionId);
            // flow 2: the nav recording tile keys on
            // `activeSessionIdAtom`, which only a recording START sets — a
            // pure-offline connect has no start this run, so bind it here
            // (idempotent for flow 1, whose start already set it).
            setActiveSessionId(result.sessionId);
            if (result.recordingInfo) {
              setDeviceRecordingInfo(result.recordingInfo);
            }
            seedRecoveredTimer(result.seedSecs, result.isPaused);
          }

          // Only a resolved match/format is valid against the Iglu schema's
          // enum — a deferred/failed bind reports 'unknown' for either field,
          // which `trackHardwareRecordingRecovered` must never be sent.
          if (
            event.payload.recoveredMatchVia !== 'unknown' &&
            event.payload.recoveredFormat !== 'unknown'
          ) {
            trackHardwareRecordingRecovered({
              sessionIdLocal: result.sessionId ?? 'unknown',
              recoveredMatchVia: event.payload.recoveredMatchVia,
              recoveredFormat: event.payload.recoveredFormat,
              recoveredDurationSeconds: event.payload.durationSecs,
            });
          }
        });

        if (cancelled) {
          unlisten();
          return;
        }
        unlistenFn = unlisten;
      } catch (error) {
        logger.error({
          msg: 'Failed to setup Chronicle recording_recovered listener',
          error: error instanceof Error ? error.message : String(error),
        });
      }
    };

    void setup();

    return () => {
      cancelled = true;
      unlistenFn?.();
    };
  }, [
    setLiveOffline,
    setChronicleSessionId,
    setActiveSessionId,
    setDeviceRecordingInfo,
    setReconnectedPendingEnd,
    setPureOfflineSessions,
    setRecordingEndReason,
    setRemoteStatusCard,
    jotaiStore,
    wasRecordingRef,
  ]);
}
